import React from 'react';

import { ReactComponent as Scissors } from '@/assets/icons/scissors.svg';

import { FeedbackGraphic } from '@/components/FeedbackGraphic';
import InputField from '@/components/InputField';
import Spinner from '@/components/Spinner';
import TooltipOverflow from '@/components/TooltipOverflow';

import { Solicitacao } from '@/models/Solicitacao';

import InputFieldQuantity from '@/features/recursos/procedimentos/components/InputFieldQuantity';
import { codeableConceptDisplay } from '@/features/fhir/helpers/fhirpath';
import {
  deviceName,
  deviceQuantityValue,
  setDevicePropertyQuantity,
} from '@/features/fhir/helpers/device';

import {
  Container,
  Heading,
  LoadingContent,
  LoadingText,
  OPMEBox,
  OPMEBoxContent,
  OPMEBoxFeedback,
  OPMEGroup,
  OPMEGroupTitle,
} from './styles';
import { SharedDevices, groupSharedDevicesByProcedureIds } from './helpers';

interface Props {
  procedimentos: Solicitacao[];
  disabled?: boolean;
  onSharedDevicesChange?: (shared: SharedDevices[]) => void;
}

const CardSharedDevices: React.FC<Props> = ({
  procedimentos,
  disabled = false,
  onSharedDevicesChange,
}) => {
  const opmeBoxRef = React.useRef<HTMLDivElement>(null);

  const serviceRequests = React.useMemo(() => {
    return procedimentos.map(
      procedimento => procedimento.pedido as fhir4.ServiceRequest,
    );
  }, [procedimentos]);

  const [shared, setGroups] = React.useState<SharedDevices[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [loadingMinHeight, setLoadingMinHeight] = React.useState<number>(180);

  const emptyBoxText = React.useMemo(() => {
    if (serviceRequests.length === 0) {
      return 'As OPMEs serão sugeridas após a seleção dos procedimentos.';
    }

    if (shared.length === 0) {
      return 'Nenhuma OPME sugerida para os procedimentos selecionados.';
    }

    return '';
  }, [serviceRequests.length, shared.length]);

  React.useEffect(() => {
    let isCancelled = false;

    const loadingStart = Date.now();
    const currentBoxHeight = opmeBoxRef.current?.offsetHeight;

    if (currentBoxHeight) {
      setLoadingMinHeight(currentBoxHeight);
    }

    setIsLoading(true);

    const grouped = groupSharedDevicesByProcedureIds(serviceRequests);
    const loadingRemaining = Math.max(300 - (Date.now() - loadingStart), 0);

    const timeoutId = window.setTimeout(() => {
      if (isCancelled) {
        return;
      }

      setGroups(grouped);
      setIsLoading(false);
    }, loadingRemaining);

    return () => {
      isCancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [serviceRequests]);

  React.useEffect(() => {
    if (onSharedDevicesChange) {
      onSharedDevicesChange(shared);
    }
  }, [shared, onSharedDevicesChange]);

  const handleQuantityChange = React.useCallback(
    (device: fhir4.Device, value: number) => {
      setGroups(currentGroups =>
        currentGroups.map(group => ({
          ...group,
          devices: group.devices.map(d =>
            d.id === device.id
              ? setDevicePropertyQuantity(d, 'quantity', value)
              : d,
          ),
        })),
      );
    },
    [],
  );

  const handleRemoveDevice = React.useCallback((device: fhir4.Device) => {
    setGroups(currentGroups =>
      currentGroups
        .map(group => ({
          ...group,
          devices: group.devices.filter(d => d.id !== device.id),
        }))
        .filter(group => group.devices.length > 0),
    );
  }, []);

  const groupTitle = React.useCallback(
    (group: SharedDevices) => {
      if (group.procedureIds.length > 0) {
        const groupRequests = serviceRequests.filter(
          request => request.id && group.procedureIds.includes(request.id),
        );

        return (
          <OPMEGroupTitle>
            {groupRequests.map((request, index, array) => {
              const title = codeableConceptDisplay(request, 'code');

              return (
                <React.Fragment key={request.id}>
                  {index > 0 && ' | '}

                  {array.length > 1 ? (
                    <TooltipOverflow title={title} />
                  ) : (
                    <span>{title}</span>
                  )}
                </React.Fragment>
              );
            })}
          </OPMEGroupTitle>
        );
      }

      return <></>;
    },
    [serviceRequests],
  );

  let opmeContent = (
    <OPMEBoxContent>
      {shared.map(group => (
        <OPMEGroup key={group.procedureIds.join('|')}>
          <thead>
            <tr>
              <td>{groupTitle(group)}</td>
              <td>Quantidade</td>
            </tr>
          </thead>
          <tbody>
            {group.devices.map(device => (
              <tr key={[...group.procedureIds, device.id].join('|')}>
                <td>
                  <InputField readOnly value={deviceName(device)} />
                </td>
                <td>
                  <InputFieldQuantity
                    readOnly={disabled}
                    minValue={deviceQuantityValue(device, 'min-quantity') || 1}
                    maxValue={deviceQuantityValue(device, 'max-quantity')}
                    initialValue={deviceQuantityValue(device, 'quantity') || 1}
                    onChange={value => handleQuantityChange(device, value)}
                    onRemoveItem={() => handleRemoveDevice(device)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </OPMEGroup>
      ))}
    </OPMEBoxContent>
  );

  if (isLoading) {
    opmeContent = (
      <OPMEBoxContent>
        <LoadingContent style={{ minHeight: loadingMinHeight }}>
          <Spinner size={24} />
          <LoadingText>Carregando OPME</LoadingText>
        </LoadingContent>
      </OPMEBoxContent>
    );
  } else if (shared.length === 0) {
    opmeContent = (
      <OPMEBoxFeedback>
        <FeedbackGraphic graphic="empty-box" title={emptyBoxText} />
      </OPMEBoxFeedback>
    );
  }

  return (
    <Container>
      <Heading>
        <Scissors />
        OPMEs sugeridas
      </Heading>

      <OPMEBox ref={opmeBoxRef}>{opmeContent}</OPMEBox>
    </Container>
  );
};

export default CardSharedDevices;
